import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
  Spinner,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

export default function JurnalTvaIesiriIncasare() {
  const navigate = useNavigate();

  const today = new Date();
  const yyyy = today.getFullYear();

  const [from, setFrom] = useState(new Date(yyyy, 0, 1));
  const [to, setTo] = useState(today);
  const [q, setQ] = useState("");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState({ rows: [], totals_by_cota: [] });
  const [loaded, setLoaded] = useState(false);

  const formatDateString = (date) => {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const fmt2 = (n) =>
    Number(n ?? 0).toLocaleString("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const load = async () => {
    setBusy(true);
    setErr("");
    setLoaded(false);
    try {
      const fromStr = formatDateString(from);
      const toStr = formatDateString(to);
      const res = await authFetch(
        `/rapoarte/jurnal-tva-iesiri-incasare?from=${fromStr}&to=${toStr}`
      );
      setData({
        rows: Array.isArray(res?.rows) ? res.rows : [],
        totals_by_cota: Array.isArray(res?.totals_by_cota)
          ? res.totals_by_cota
          : [],
      });
      setLoaded(true);
    } catch (e) {
      setErr(e?.message || "Eroare la încărcare Jurnal TVA vânzări la încasare.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    // don't auto load on mount
  }, []);

  const filteredRows = useMemo(() => {
    const rows = data.rows || [];
    const s = (q || "").trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => {
      const a = String(r.doc_no ?? "").toLowerCase();
      const b = String(r.partener ?? "").toLowerCase();
      const c = String(r.cui ?? "").toLowerCase();
      const d = String(r.plata_doc_no ?? "").toLowerCase();
      return (
        a.includes(s) ||
        b.includes(s) ||
        c.includes(s) ||
        d.includes(s)
      );
    });
  }, [data.rows, q]);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const r of filteredRows) {
      const k = `${r.doc_id}-${r.plata_doc_no}-${r.plata_doc_date}`;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    }
    const arr = Array.from(m.entries()).map(([key, rows]) => {
      rows.sort((a, b) => Number(a.cota ?? 0) - Number(b.cota ?? 0));
      return { key, rows };
    });

    arr.sort((A, B) => {
      const a0 = A.rows[0] || {};
      const b0 = B.rows[0] || {};
      const dp_a = String(a0.plata_doc_date || "");
      const dp_b = String(b0.plata_doc_date || "");
      if (dp_a !== dp_b) return dp_a.localeCompare(dp_b);
      return String(a0.plata_doc_no || "").localeCompare(String(b0.plata_doc_no || ""));
    });

    return arr;
  }, [filteredRows]);

  const totalsGeneral = useMemo(() => {
    const t = data.totals_by_cota || [];
    return {
      baza: t.reduce((s, x) => s + Number(x.baza || 0), 0),
      tva: t.reduce((s, x) => s + Number(x.tva || 0), 0),
      total: t.reduce((s, x) => s + Number(x.total || 0), 0),
    };
  }, [data.totals_by_cota]);

  return (
    <Card className="shadow-sm border-0 rounded-4">
      <Card.Body className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="fw-bold text-dark mb-1">
              Jurnal TVA Vânzări (la încasare)
            </h4>
            <p className="text-muted small mb-0">
              Facturi de vânzare cu opțiune de TVA la încasare încasate în intervalul selectat
            </p>
          </div>
          <div className="d-flex align-items-center gap-2 d-print-none">
            <Button variant="outline-primary" size="sm" className="fw-semibold shadow-sm me-2" onClick={() => window.print()}>
              <i className="bi bi-printer me-1"></i> Tipărește
            </Button>
            <div className="bg-light px-3 py-2 rounded-3 text-muted small">
              {filteredRows.length} poziții • {grouped.length} documente încasare
            </div>
          </div>
        </div>

        <Form className="bg-light p-3 rounded-4 mb-4 border border-light-subtle d-print-none">
          <Row className="align-items-end g-3">
            <Col xs={12} md={3}>
              <Form.Label className="fw-semibold text-secondary small mb-1">De la</Form.Label>
              <DatePicker
                selected={from}
                onChange={(date) => setFrom(date)}
                dateFormat="dd.MM.yyyy"
                className="form-control border-0 shadow-sm"
              />
            </Col>
            <Col xs={12} md={3}>
              <Form.Label className="fw-semibold text-secondary small mb-1">Până la</Form.Label>
              <DatePicker
                selected={to}
                onChange={(date) => setTo(date)}
                dateFormat="dd.MM.yyyy"
                className="form-control border-0 shadow-sm"
              />
            </Col>
            <Col xs={12} md={4}>
              <Form.Label className="fw-semibold text-secondary small mb-1">Căutare rapidă</Form.Label>
              <Form.Control
                placeholder="Factură / Partener / CUI / Document încasare"
                className="border-0 shadow-sm"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </Col>
            <Col xs={12} md={2}>
              <Button onClick={load} disabled={busy} className="w-100 fw-semibold shadow-sm">
                {busy ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Încărcare...
                  </>
                ) : (
                  "Reîncarcă"
                )}
              </Button>
            </Col>
          </Row>
        </Form>

        {err && (
          <Alert variant="danger" className="mt-3 mb-4 rounded-3 border-0 shadow-sm">
            {err}
          </Alert>
        )}

        <div className="table-responsive">
          {busy ? (
            <div className="py-5 text-center">
              <Spinner animation="border" variant="primary" />
              <div className="mt-2 text-muted small">Se procesează datele...</div>
            </div>
          ) : (
            <Table hover bordered responsive size="sm" className="align-middle rounded-3 overflow-hidden">
              <thead className="bg-primary text-white">
                <tr>
                  <th colSpan={4} className="text-center border-bottom-0 py-2 table-primary text-dark fw-bold">Factură Ieșire</th>
                  <th colSpan={3} className="text-center border-bottom-0 py-2 table-info text-dark fw-bold">Document Încasare</th>
                  <th colSpan={4} className="text-center border-bottom-0 py-2 table-success text-dark fw-bold">Sume TVA la Încasare</th>
                </tr>
                <tr>
                  <th style={{ minWidth: 100 }}>Dată</th>
                  <th style={{ minWidth: 100 }}>Număr</th>
                  <th>Partener</th>
                  <th style={{ minWidth: 110 }}>CUI</th>
                  <th style={{ minWidth: 100 }}>Dată doc</th>
                  <th style={{ minWidth: 100 }}>Număr doc</th>
                  <th className="text-end" style={{ minWidth: 120 }}>Total încasat</th>
                  <th className="text-end" style={{ minWidth: 60 }}>Cotă</th>
                  <th className="text-end" style={{ minWidth: 120 }}>Bază încasată</th>
                  <th className="text-end" style={{ minWidth: 120 }}>TVA încasat</th>
                  <th className="text-end" style={{ minWidth: 120 }}>Total încasat</th>
                </tr>
              </thead>

              <tbody>
                {grouped.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-center text-muted py-5 bg-white">
                      {loaded
                        ? "Nu s-au găsit facturi încasate în perioada selectată."
                        : "Alegeți parametrii și apăsați \"Reîncarcă\"."}
                    </td>
                  </tr>
                ) : (
                  grouped.map(({ key, rows }) => {
                    const first = rows[0] || {};
                    const subBaza = rows.reduce(
                      (s, r) => s + Number(r.baza || 0),
                      0
                    );
                    const subTva = rows.reduce(
                      (s, r) => s + Number(r.tva || 0),
                      0
                    );
                    const subTot = rows.reduce(
                      (s, r) => s + Number(r.total || 0),
                      0
                    );

                    const span = rows.length + 1;

                    return (
                      <React.Fragment key={key}>
                        {rows.map((r, idx) => (
                          <tr key={`${key}-${r.cota}-${idx}`}>
                            {idx === 0 && (
                              <>
                                <td rowSpan={span} className="bg-white">{first.doc_date}</td>
                                <td rowSpan={span} className="bg-white">
                                  <span className="fw-semibold text-primary text-dark-print cursor-pointer" style={{ cursor: "pointer" }}
                                    onClick={() =>
                                      navigate(`/iesire/${r.doc_id}/edit`)
                                    }
                                    title="Deschide factura"
                                  >
                                    {first.doc_no || ""}
                                  </span>
                                </td>
                                <td rowSpan={span} className="bg-white">{first.partener || "-"}</td>
                                <td rowSpan={span} className="bg-white">{first.cui || ""}</td>
                                <td rowSpan={span} className="bg-white">{first.plata_doc_date}</td>
                                <td rowSpan={span} className="bg-white">{first.plata_doc_no || ""}</td>
                                <td rowSpan={span} className="text-end fw-semibold bg-white">
                                  {fmt2(first.plata_amount)}
                                </td>
                              </>
                            )}

                            <td className="text-end text-secondary fw-semibold">{Number(r.cota ?? 0)}%</td>
                            <td className="text-end">{fmt2(r.baza)}</td>
                            <td className="text-end">{fmt2(r.tva)}</td>
                            <td className="text-end">{fmt2(r.total)}</td>
                          </tr>
                        ))}

                        <tr className="table-light fw-semibold">
                          <td className="text-end bg-light small py-1">Subtotal</td>
                          <td className="text-end bg-light small py-1">{fmt2(subBaza)}</td>
                          <td className="text-end bg-light small py-1">{fmt2(subTva)}</td>
                          <td className="text-end bg-light small py-1">{fmt2(subTot)}</td>
                        </tr>
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>

              <tfoot className="table-secondary border-top border-dark-subtle">
                {(data.totals_by_cota || []).map((t) => (
                  <tr key={`tot-${t.cota}`} className="border-0">
                    <td colSpan={7} className="border-0 bg-transparent"></td>
                    <td className="text-end fw-semibold bg-transparent border-start-0">
                      Total {Number(t.cota ?? 0)}%
                    </td>
                    <td className="text-end fw-semibold">{fmt2(t.baza)}</td>
                    <td className="text-end fw-semibold">{fmt2(t.tva)}</td>
                    <td className="text-end fw-semibold">{fmt2(t.total)}</td>
                  </tr>
                ))}

                <tr className="table-dark text-white fw-bold">
                  <td colSpan={7} className="text-end py-2">TOTAL GENERAL VÂNZĂRI LA ÎNCASARE</td>
                  <td className="text-end py-2">TOTAL</td>
                  <td className="text-end py-2">{fmt2(totalsGeneral.baza)}</td>
                  <td className="text-end py-2">{fmt2(totalsGeneral.tva)}</td>
                  <td className="text-end py-2">{fmt2(totalsGeneral.total)}</td>
                </tr>
              </tfoot>
            </Table>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
